// tableRendering.js
//
// The look of the platform's data grids: the Data page grid (DataTable) and every data
// preview (PreviewTable, which the Decode Agent's chat tables also use). Each grid keeps its
// own markup; what is here is the styling and the column-width element they share, so the
// grids draw their headers, rows and cells identically.
//
// Body cells are plain <td> elements carrying class names, styled once through the table
// body's own class (gridBodyStyles), rather than MUI TableCells each processing an `sx` of
// their own: building a TableCell cost about 35µs, and a scroll step on a 94-column table
// builds 2,350 of them.

import React from 'react';
import { alpha } from '@mui/material/styles';

export const BODY_CELL_FONT_SIZE = '0.875rem';

// Header cells: semi-bold labels on the paper background with divider borders, one line cut
// off with an ellipsis at the column's fixed width. Each grid adds its own pinning.
export const gridHeaderCellStyles = (theme) => ({
  fontWeight: 600,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  py: 1.5,
  px: 1,
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRight: `1px solid ${theme.palette.divider}`,
});

// The body's rows and cells, for the table body's `sx`. A row carries `body-row`, plus
// `striped-row` when its position in the whole data is even: stripes follow the data, not
// the DOM, because the scroll window moves rows in and out. A cell carries `data-cell`, plus
// `align-right` for numbers, `identifier-cell` (and `identifier-cell-alternate` on unstriped
// rows) for a pinned identifier column, and `lineage-cell` when clicking it opens the
// cell's history.
//
// Cells reproduce the MUI TableCell they replace: the theme's body2 type at the body cell
// size, 8px padding and the divider borders. Each cell is a single line cut off with an
// ellipsis, since the scroll window assumes every row is one line tall. Each cell rule names
// one class, so between rules of equal weight the later one wins: an identifier cell's solid
// background over the default `inherit`, and a clickable cell's hover colour over both.
export const gridBodyStyles = (theme) => ({
  '& tr.body-row': {
    transition: 'background-color 0.15s ease-in-out',
  },
  '& tr.striped-row': {
    backgroundColor: alpha(theme.palette.background.default, 0.3),
  },
  '& tr.body-row:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  '& td.data-cell': {
    ...theme.typography.body2,
    fontSize: BODY_CELL_FONT_SIZE,
    color: theme.palette.text.primary,
    verticalAlign: 'inherit',
    textAlign: 'left',
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
    backgroundColor: 'inherit',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    cursor: 'default',
    transition: 'background-color 0.15s ease-in-out',
  },
  '& td.align-right': {
    textAlign: 'right',
  },
  // A pinned identifier column stays in view while scrolling sideways, so it needs a
  // background covering the cells that pass beneath it, slightly transparent to hint at
  // the scroll.
  '& td.identifier-cell': {
    position: 'sticky',
    left: 0,
    zIndex: 100,
    backgroundColor: alpha(theme.palette.background.paper, 0.98),
  },
  '& td.identifier-cell-alternate': {
    backgroundColor: alpha(theme.palette.background.default, 0.98),
  },
  '& td.lineage-cell': {
    cursor: 'pointer',
  },
  '& td.lineage-cell:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
});

// One <col> per leaf column, carrying the width useColumnWidths computed for it. A grid using
// these sets `tableLayout: 'fixed'` on its table: the browser then takes every column's width
// from here and never measures the cells, so rows entering the scroll window cannot move a
// column.
export function ColumnWidthGroup({ leafOrder, columnWidths }) {
  return (
    <colgroup>
      {leafOrder.map((leafId) => (
        <col key={leafId} style={{ width: columnWidths[leafId] }} />
      ))}
    </colgroup>
  );
}
